import React, { useEffect, useState, useRef } from 'react';
import { Container, Grid, Card, CardContent, CardMedia, Typography, Button, Box, CircularProgress, Skeleton } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Experience, getExperiences } from '../services/experienceService';
import { useTranslation } from 'react-i18next';
import SEO from '../components/SEO';
import { getBreadcrumbSchema, getFAQPageSchema } from '../components/StructuredData';

interface ExperienceTranslation {
  title: string;
  description: string;
  includes: string[];
}

interface ExperienceCardProps {
  experience: Experience;
  translation: ExperienceTranslation;
  onBook: (experience: Experience) => void;
}

const ExperienceCard: React.FC<ExperienceCardProps> = ({ experience, translation, onBook }) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const { t } = useTranslation();
  const imgRef = useRef<HTMLImageElement>(null);

  const getFullImagePath = (relativePath: string): string => {
    if (relativePath.startsWith('http')) {
      return relativePath;
    }
    const path = relativePath.startsWith('/') ? relativePath : `/${relativePath}`;
    return `${window.location.origin}${path}`;
  };

  useEffect(() => {
    const img = new Image();
    img.src = getFullImagePath(experience.image);
    img.onload = () => {
      setImageLoaded(true);
    };
    img.onerror = () => {
      console.error(`Failed to load image: ${experience.image}`);
      setImageError(true);
    };
    return () => {
      img.onload = null;
      img.onerror = null;
    };
  }, [experience.image]);

  return (
    <Card sx={{ 
      height: '100%', 
      display: 'flex', 
      flexDirection: 'column',
      transition: 'transform 0.3s, box-shadow 0.3s',
      '&:hover': {
        transform: 'translateY(-8px)',
        boxShadow: 8
      }
    }}>
      <Box sx={{ position: 'relative', paddingTop: '56.25%' }}>
        {!imageLoaded && !imageError && (
          <Skeleton
            animation="wave"
            variant="rectangular"
            sx={{ 
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%'
            }}
          />
        )}
        
        {imageError ? (
          <Box 
            sx={{ 
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              bgcolor: 'grey.200',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <Typography variant="subtitle1" color="text.secondary">
              {t('common.imageUnavailable')}
            </Typography>
          </Box>
        ) : (
          <CardMedia
            ref={imgRef}
            component="img"
            image={experience.image}
            alt={translation.title}
            sx={{ 
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              display: imageLoaded ? 'block' : 'none'
            }}
            loading="lazy"
          />
        )}
      </Box>
      
      <CardContent sx={{ flexGrow: 1 }}>
        <Typography gutterBottom variant="h5" component="h3">
          {translation.title}
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          {translation.description}
        </Typography>
        <Box sx={{ mb: 2 }}>
          {translation.includes.map((item, index) => (
            <Typography key={index} variant="body2" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              ✓ {item}
            </Typography>
          ))}
        </Box>
        <Typography variant="h6" color="primary" gutterBottom>
          {t('common.from')}{t('common.currency')}{experience.price}
        </Typography>
        <Button 
          variant="contained" 
          color="primary" 
          fullWidth
          onClick={() => onBook(experience)}
        >
          {t('experiences.bookNow')}
        </Button>
      </CardContent>
    </Card>
  );
};

const Experiences: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [experiences, setExperiences] = useState<Experience[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchExperiences = async () => {
      try {
        const data = await getExperiences();
        const preloadPromises = data.map(exp => {
          return new Promise<void>((resolve) => {
            const img = new Image();
            img.src = exp.image;
            img.onload = () => resolve();
            img.onerror = () => resolve();
          });
        });
        Promise.all(preloadPromises).then(() => {
          setExperiences(data);
          setLoading(false);
        });
      } catch (error) {
        console.error('Error fetching experiences:', error);
        setExperiences([]);
        setLoading(false);
      }
    };
    fetchExperiences();
  }, []);

  const handleBooking = (experience: Experience) => {
    navigate('/book-experience', { state: { experience } });
  };

  const getExperienceTranslation = (experience: Experience): ExperienceTranslation => {
    return {
      title: t(experience.title),
      description: t(experience.description),
      includes: t(experience.includes, { returnObjects: true }) as string[]
    };
  };

  const faqQuestions = ((): { question: string; answer: string }[] => {
    const premiumIncludes = t('experiences.includes.premium', { returnObjects: true }) as string[];
    const standardIncludes = t('experiences.includes.standard', { returnObjects: true }) as string[];
    const basicIncludes = t('experiences.includes.basic', { returnObjects: true }) as string[];

    return [
      {
        question: t('experiences.premium.title'),
        answer: t('experiences.premium.description') + ' ' + premiumIncludes.join(', ')
      },
      {
        question: t('experiences.standard.title'),
        answer: t('experiences.standard.description') + ' ' + standardIncludes.join(', ')
      },
      {
        question: t('experiences.basic.title'),
        answer: t('experiences.basic.description') + ' ' + basicIncludes.join(', ')
      }
    ];
  })();

  if (loading) {
    return (
      <Container sx={{ py: 8, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container sx={{ py: 8 }}>
      <SEO
        title={t('seo.experiences.title')}
        description={t('seo.experiences.description')}
        canonicalPath="/experiences"
        jsonLd={[
          getBreadcrumbSchema([
            { name: 'Home', url: '/' },
            { name: t('experiences.title'), url: '/experiences' },
          ]),
          getFAQPageSchema(faqQuestions),
        ]}
      />

      <Typography variant="h3" component="h1" gutterBottom align="center">
        {t('experiences.title')}
      </Typography>
      <Typography variant="h6" color="text.secondary" paragraph align="center">
        {t('experiences.subtitle')}
      </Typography>
      
      <Grid container spacing={4}>
        {experiences.map((experience) => {
          const translation = getExperienceTranslation(experience);
          return (
            <Grid item key={experience.id} xs={12} sm={6} md={4}>
              <ExperienceCard 
                experience={experience} 
                translation={translation} 
                onBook={handleBooking}
              />
            </Grid>
          );
        })}
      </Grid>
    </Container>
  );
};

export default Experiences;
